import { Component, OnInit } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { DataService } from 'src/app/data.service';

/**
 *  ADMINISZTRÁTORI JOGOSULTSÁGOK.
 *
 *  „Admin vagyok, mégis le van zárva előttem." Ez a képernyő erre válaszol. Eddig
 *  nyolc modul döntötte el külön-külön, kódba írva, hogy ki számít adminnak – az
 *  intézmény nem tudta megnézni és nem tudta átállítani. Most egy helyen áll, és
 *  a képernyő azt is megmondja, hogy TE most minek számítasz, és miért.
 */
@Component({
  selector: 'app-admin-jogosultak',
  templateUrl: './jogosultak.component.html',
  styleUrls: ['./jogosultak.component.scss'],
})
export class AdminJogosultakComponent implements OnInit {
  toltodik = true;
  intezmeny = '';
  alrendszer = 'SULINFORM';

  teruletek: any[] = [];
  tetelek: any[] = [];
  onellenorzes: any = null;

  // Új bejegyzés
  ujTerulet = '*';
  ujFajta: 'jogkor' | 'felhasznalo' = 'jogkor';
  ujErtek = '';
  ujMegjegyzes = '';
  ment = false;

  jogkorok: any[] = [];
  munkatarsak: any[] = [];
  szuro = '';

  constructor(private http: HttpClient, private data: DataService) {}

  async ngOnInit() {
    const si = await this.data.getFromStorage('sajatIntezmeny');
    this.intezmeny = String(si?.intezmenyId || '').replace(/^\/+|\/+$/g, '');
    await this.betolt();
  }

  get intezmenyPath(): string { return encodeURI(this.intezmeny); }

  async betolt() {
    this.toltodik = true;
    try {
      const [teruletek, tetelek] = await Promise.all([
        firstValueFrom(this.http.get<any[]>('/api/admin/jogosultak/teruletek')),
        firstValueFrom(this.http.get<any[]>(`/api/admin/jogosultak/lista?alrendszer=${this.alrendszer}`)),
      ]);
      this.teruletek = (teruletek || []).filter((t) => t.alrendszer === this.alrendszer);
      this.tetelek = tetelek || [];
      await this.onellenorzesBetolt();
      await this.valasztoListak();
    } catch (e: any) {
      this.data.sendToast(e?.error?.error || 'A jogosultságok nem tölthetők be.', 'error');
    } finally {
      this.toltodik = false;
    }
  }

  async onellenorzesBetolt() {
    if (!this.intezmeny) return;
    try {
      this.onellenorzes = await firstValueFrom(this.http.get(
        `/api/admin/jogosultak/onellenorzes/${this.intezmenyPath}?alrendszer=${this.alrendszer}`));
    } catch (e) { this.onellenorzes = null; }
  }

  /*  A választható jogkörök és munkatársak: gépelés helyett listából. Egy elgépelt
   *  jogkörnév néma hiba lenne – felvenni felveszi, csak sosem illeszkedik. */
  async valasztoListak() {
    try {
      this.jogkorok = await firstValueFrom(this.http.get<any[]>(
        `/api/jogosultsag/jogkorok/${this.alrendszer}/${this.intezmenyPath}`)) || [];
    } catch (e) { this.jogkorok = []; }
    try {
      const all: any = await firstValueFrom(this.data.getAllomany(this.intezmenyPath));
      this.munkatarsak = Array.isArray(all) ? all : [];
    } catch (e) { this.munkatarsak = []; }
  }

  teruletNev(t: string): string {
    return this.teruletek.find((x) => x.terulet === t)?.nev || t;
  }
  teruletLeiras(t: string): string {
    return this.teruletek.find((x) => x.terulet === t)?.leiras || '';
  }

  tetelekTerulethez(t: string): any[] {
    return this.tetelek.filter((x) => x.terulet === t);
  }

  /** A jogkör emberi neve; ha nincs a listában, maga az azonosító. */
  jogkorNev(ertek: string): string {
    return this.jogkorok.find((j) => String(j.jogkor) === String(ertek))?.name || ertek;
  }

  felhasznaloNev(ertek: string): string {
    const m = this.munkatarsak.find((x: any) => String(x.adid) === String(ertek));
    return m ? `${m.viselt_vez || ''} ${m.viselt_ker || ''}`.trim() : ertek;
  }

  get szurtMunkatarsak(): any[] {
    const nev = (m: any) => `${m?.viselt_vez || ''} ${m?.viselt_ker || ''}`.trim();
    const k = this.szuro.trim().toLowerCase();
    return (this.munkatarsak || [])
      .filter((m: any) => !k || nev(m).toLowerCase().includes(k) || String(m?.adid || '').toLowerCase().includes(k))
      .sort((a: any, b: any) => nev(a).localeCompare(nev(b), 'hu'))
      .slice(0, 200);
  }

  async felvesz() {
    if (this.ment || !this.ujErtek) return;
    this.ment = true;
    try {
      await firstValueFrom(this.http.post('/api/admin/jogosultak', {
        alrendszer: this.alrendszer, terulet: this.ujTerulet,
        fajta: this.ujFajta, ertek: this.ujErtek,
        megjegyzes: this.ujMegjegyzes || null,
      }));
      this.ujErtek = ''; this.ujMegjegyzes = ''; this.szuro = '';
      this.data.sendToast('Felvéve. Azonnal érvényes.', 'success');
      await this.betolt();
    } catch (e: any) {
      this.data.sendToast(e?.error?.error || 'A felvétel nem sikerült.', 'error');
    } finally {
      this.ment = false;
    }
  }

  async torol(t: any) {
    const mi = t.fajta === 'jogkor'
      ? `a(z) „${this.jogkorNev(t.ertek)}" jogkör`
      : `${this.felhasznaloNev(t.ertek)}`;
    if (!confirm(`Biztosan elveszed innen: ${mi} — ${this.teruletNev(t.terulet)}?`)) return;
    try {
      await firstValueFrom(this.http.delete(`/api/admin/jogosultak/${t.id}`));
      this.data.sendToast('Törölve.', 'success');
      await this.betolt();
    } catch (e: any) {
      this.data.sendToast(e?.error?.error || 'A törlés nem sikerült.', 'error');
    }
  }

  /** A saját állapot magyarázata – ez a képernyő legfontosabb mondata. */
  okSzoveg(t: any): string {
    if (t.admin && t.ok === 'jogkor') return `igen — a(z) „${this.jogkorNev(t.ertek)}" jogkör miatt`;
    if (t.admin && t.ok === 'felhasznalo') return 'igen — név szerint fel vagy véve';
    if (t.admin && t.ok === 'jogkor-jelolo') return 'igen — a jogkörödön be van jelölve az „admin" pipa';
    if (t.ok === 'nem-munkatars') return 'nem — ebben az intézményben nincs feladatellátásod';
    return 'nem — sem a jogköröd, sem a neved nincs felvéve ide';
  }
}
